import Vue, { VueConstructor } from 'vue';
import { Component } from 'vue-property-decorator';

import { getUserProfile } from '@monjin/data/service/user';

import AboutCompany from './AboutCompany/AboutCompany';
import AboutYourself from './AboutYourself/AboutYourself';
import CheckProgress from './CheckProgress/CheckProgress';
import RegistrationForm from './RegistrationForm/RegistrationForm';
import TeamName from './TeamName/TeamName';
import UploadLogo from './UploadLogo/UploadLogo';
import VerifyEmail from './VerifyEmail/VerifyEmail';
import Welcome from './Welcome/Welcome';

import { directLogin, provisionTenant, uploadOrgLogo } from '@api/employerRegistration';
import { matchError } from '@api/util';
import { OrganizationRegistration, VerifyEmailData } from '@model/employer';

import WithRender from './OrgRegistration.html';

// The plan is to show step by step wizard based on step number.
// Step = 0 Register and set password
// Step = 1 about company
// Step = 2 about yourself
// Step = 3 Team name selection
// Step = 4 Verify email
// Step = 5 check progress
// Step = 6 upload company logo

const WIZARD_STEPS = [ RegistrationForm, AboutCompany, AboutYourself, TeamName, VerifyEmail, CheckProgress, UploadLogo, Welcome ];

@WithRender
@Component({})
export class OrgRegistration extends Vue {

    public resourceBaseUrl: string = '';

    public step: number = 0;

    public verifyEmailData: VerifyEmailData = {
        email: '',
        requestId: ''
    };

    public orgData: OrganizationRegistration = {
        name: '',
        subDomain: '',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminDialCode: '',
        adminPhoneNumber: '',
        adminTimezoneId: -1,
        countryId: -1,
        countryName: '',
        defaultIndustryId: -1,
        industryName: '',
        numberOfEmployees: 1,
        password: '',
        roleInOrganization: '',
        // role: {
        //     id: -1,
        //     jobFunctionId: -1,
        //     value: ''
        // },
        industry: {
            id: -1,
            value: ''
        },
        country: {
            id: -1,
            value: '',
            dialCode: -1
        },
        timezone: {
            id: -1,
            name: '',
            offset: 0
        },

        productPreference: '',
        additionalQueryParams: ''
    };

    public created() {
        window.onbeforeunload = this.refreshHandler;
    }

    public refreshHandler() {
        if (this.step !== 7) {
            return confirm();
        }
    }

    public get selectedView(): VueConstructor {
        return WIZARD_STEPS[this.step];
    }

    public onStateComplete(stepNumber: number, data: any) {

        switch (stepNumber) {
            case 1:
                this.orgData.adminEmail = data.email;
                this.orgData.password = data.password;

                this.step = stepNumber;
                break;

            case 2:
                this.orgData.name = data.name;
                this.orgData.countryId = data.country.id;
                this.orgData.countryName = data.country.value;
                this.orgData.industryName = data.industry.value;
                this.orgData.defaultIndustryId = data.industry.id;
                this.orgData.numberOfEmployees = data.count;
                // this.orgData.roleInOrganization = data.role.value;
                // this.orgData.role = data.role;
                this.orgData.industry = data.industry;
                this.orgData.country = data.country;
                this.orgData.adminDialCode = data.country.dialCode;

                this.step = stepNumber;
                break;

            case 3:
                this.orgData.adminFirstName = data.firstName;
                this.orgData.adminLastName = data.lastName;
                this.orgData.adminDialCode = data.mobileNumber.dialCode;
                this.orgData.adminPhoneNumber = data.mobileNumber.dialNumber;
                this.orgData.adminTimezoneId = data.timezone.id;
                this.orgData.timezone = data.timezone;

                this.step = stepNumber;
                break;

            case 4:
                this.orgData.subDomain = data;

                // delete this.orgData.role;
                delete this.orgData.industry;
                delete this.orgData.country;
                delete this.orgData.timezone;

                // `pref` query parameter comes from Monjin website.
                // Possible values are: OneWay_Lite, OneWay_Essential, OneWay_Professional,
                // MonjinBasics_Lite, MonjinBasics_Essential, MonjinBasics_Professional,
                // OCOI_Lite, OCOI_Essential, MonjinSuite
                this.orgData.productPreference = this.$route.query.pref as any;
                this.orgData.additionalQueryParams = new URLSearchParams(this.$route.query as any).toString();

                provisionTenant(this.orgData)
                    .then((response) => {
                        this.verifyEmailData.requestId = response.requestId;
                        this.verifyEmailData.email = response.adminEmail;

                        this.step = stepNumber;
                    })
                    .catch(matchError({
                        anyError: () => {
                            const message = 'Something went wrong! Please try again later.';
                            this.$snackbar.error({ message });
                        }
                    }));
                break;

            case 5:
                this.step = stepNumber;
                break;

            case 6:
                directLogin(this.orgData.adminEmail, this.orgData.password)
                    .then(() => getUserProfile())
                    .then((x) => {
                        this.resourceBaseUrl = x.resourceBaseUrl;
                        this.step = stepNumber;
                    })
                    .catch(matchError({
                        anyError: () => {
                            const message = 'Something went wrong! Please try again later';
                            this.$snackbar.error({ message });
                        }
                    }));
                break;

            case 7:
                if (data !== '') {
                    uploadOrgLogo(this.verifyEmailData.requestId, data)
                        .then(() => {
                            this.step = stepNumber;
                        })
                        .catch(matchError({
                            anyError: () => {
                                const message = 'Something went wrong! Please try again later';
                                this.$snackbar.error({ message });
                            }
                        }));
                } else {
                    this.step = stepNumber;
                }
                break;

            case 8:
                const path = window.location.origin + '/orgs';
                window.location.href = path;
                break;
        }
    }

    public goBack() {
        this.step = this.step - 1;
    }
}
